/**
 * Operator impersonation launcher. Lets a Hostesa operator open a short-lived,
 * scoped session as a member of a given hotel (for support/debugging). Gated by
 * `admin:impersonate` — renders nothing actionable without it. The session is
 * handed off to the platform app via a new tab at `/__handoff#imp=<token>`; the
 * token is never persisted here and never logged.
 *
 * Every session must be justified: the operator picks a target member, gives a
 * reason and a support ticket key, and optionally a session length. These are
 * validated client-side for fast feedback and RE-validated + clamped by the
 * backend, which is authoritative (see ImpersonateBodyDto / OperatorService).
 */

import {
  DEFAULT_IMPERSONATION_DURATION_SECONDS,
  SUPPORT_TICKET_PATTERN,
  useImpersonate,
  useOperatorTenantMembers,
} from '@hostesa/api-client';
import { useAuth } from '@hostesa/auth-client';
import { useT } from '@hostesa/i18n';
import { Button, Card, cn, Input, Label, Textarea } from '@hostesa/ui';
import { useId, useState } from 'react';

const NAME = 'truncate font-[var(--font-display)] text-sm text-[var(--color-charcoal)]';
const META = 'font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]';
const HINT = 'font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]';
const FIELD_ERROR = 'font-[var(--font-mono)] text-[11px] text-[var(--color-alert)]';

const MIN_REASON_LENGTH = 12;
const MAX_REASON_LENGTH = 500;

/** Open the platform handoff tab with the impersonation token in the URL fragment. */
function openHandoff(token: string): void {
  const base = import.meta.env.VITE_PLATFORM_BASE_URL ?? 'http://localhost:5173';
  window.open(`${base}/__handoff#imp=${encodeURIComponent(token)}`, '_blank', 'noopener');
}

export interface ImpersonateLauncherProps {
  tenantId: string;
  hotelId: string;
}

export function ImpersonateLauncher({ tenantId, hotelId }: ImpersonateLauncherProps) {
  const { t } = useT('backoffice');
  const { can } = useAuth();
  const members = useOperatorTenantMembers(tenantId);
  const impersonate = useImpersonate();
  const [selected, setSelected] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [ticket, setTicket] = useState('');
  const [durationSeconds, setDurationSeconds] = useState<number>(
    DEFAULT_IMPERSONATION_DURATION_SECONDS,
  );

  const reasonId = useId();
  const ticketId = useId();
  const durationId = useId();

  // Gate the whole control behind the impersonate permission — never expose the
  // launcher to an operator who cannot use it.
  if (!can('admin:impersonate')) {
    return (
      <Card data-testid="impersonate-launcher" className="p-4">
        <p className={META}>{t('oversight.impersonate.noPermission')}</p>
      </Card>
    );
  }

  const rows = members.data ?? [];

  // Client-side mirrors of the backend rules — the server stays authoritative.
  const trimmedReason = reason.trim();
  const normalisedTicket = ticket.trim().toUpperCase();
  const reasonValid =
    trimmedReason.length >= MIN_REASON_LENGTH && trimmedReason.length <= MAX_REASON_LENGTH;
  const ticketValid = SUPPORT_TICKET_PATTERN.test(normalisedTicket);
  const canStart = Boolean(selected) && reasonValid && ticketValid && !impersonate.isPending;

  // Only surface a field error once the operator has typed something — avoid
  // shouting validation at an untouched form.
  const showReasonError = reason.length > 0 && !reasonValid;
  const showTicketError = ticket.length > 0 && !ticketValid;

  function start() {
    if (!selected || !reasonValid || !ticketValid) return;
    impersonate.mutate(
      {
        targetUserId: selected,
        tenantId,
        hotelId,
        reason: trimmedReason,
        supportTicketId: normalisedTicket,
        durationSeconds,
      },
      { onSuccess: (res) => openHandoff(res.token) },
    );
  }

  return (
    <Card data-testid="impersonate-launcher" className="flex flex-col gap-3 p-4">
      <div className="flex flex-col gap-0.5">
        <h2 className="font-[var(--font-display)] text-sm text-[var(--color-charcoal)]">
          {t('oversight.impersonate.title')}
        </h2>
        <p className={META}>{t('oversight.impersonate.description')}</p>
      </div>

      {members.isLoading ? (
        <p className={META} data-testid="impersonate-members-loading">
          {t('oversight.impersonate.membersLoading')}
        </p>
      ) : rows.length === 0 ? (
        <p className={META} data-testid="impersonate-members-empty">
          {t('oversight.impersonate.membersEmpty')}
        </p>
      ) : (
        <ul data-testid="impersonate-members" className="flex flex-col gap-1">
          {rows.map((m) => {
            const isSelected = m.userId === selected;
            return (
              <li key={m.userId}>
                <button
                  type="button"
                  data-testid={`impersonate-member-${m.userId}`}
                  aria-pressed={isSelected}
                  onClick={() => setSelected(m.userId)}
                  className={cn(
                    'flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-left transition-colors',
                    isSelected
                      ? 'border-[var(--color-charcoal)] bg-[var(--color-surface-2)]'
                      : 'border-[var(--color-border)] hover:bg-[var(--color-line-2)]',
                  )}
                >
                  <span className={NAME}>{m.email ?? m.userId}</span>
                  <span className={META}>{`${m.role} · ${m.email ?? m.userId}`}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Justification — required before a token can be minted. */}
      <div className="flex flex-col gap-1">
        <Label htmlFor={reasonId}>{t('oversight.impersonate.reasonLabel')}</Label>
        <Textarea
          id={reasonId}
          data-testid="impersonate-reason-input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t('oversight.impersonate.reasonPlaceholder')}
          aria-invalid={showReasonError}
          aria-describedby={showReasonError ? `${reasonId}-error` : `${reasonId}-hint`}
          maxLength={MAX_REASON_LENGTH}
        />
        {showReasonError ? (
          <p
            id={`${reasonId}-error`}
            data-testid="impersonate-reason-error"
            className={FIELD_ERROR}
          >
            {t('oversight.impersonate.reasonTooShort')}
          </p>
        ) : (
          <p id={`${reasonId}-hint`} className={HINT}>
            {t('oversight.impersonate.reasonHint')}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={ticketId}>{t('oversight.impersonate.ticketLabel')}</Label>
        <Input
          id={ticketId}
          data-testid="impersonate-ticket-input"
          value={ticket}
          onChange={(e) => setTicket(e.target.value)}
          placeholder={t('oversight.impersonate.ticketPlaceholder')}
          aria-invalid={showTicketError}
          aria-describedby={showTicketError ? `${ticketId}-error` : `${ticketId}-hint`}
        />
        {showTicketError ? (
          <p
            id={`${ticketId}-error`}
            data-testid="impersonate-ticket-error"
            className={FIELD_ERROR}
          >
            {t('oversight.impersonate.ticketInvalid')}
          </p>
        ) : (
          <p id={`${ticketId}-hint`} className={HINT}>
            {t('oversight.impersonate.ticketHint')}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={durationId}>{t('oversight.impersonate.durationLabel')}</Label>
        <select
          id={durationId}
          data-testid="impersonate-duration-select"
          value={durationSeconds}
          onChange={(e) => setDurationSeconds(Number(e.target.value))}
          className={cn(
            'h-9 w-full rounded-md border border-[var(--color-input)] bg-[var(--color-alabaster)] px-3',
            'font-[var(--font-text)] text-sm text-[var(--color-charcoal)] transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:border-[var(--color-warm-stone)]',
          )}
        >
          <option value={900}>{t('oversight.impersonate.duration15')}</option>
          <option value={1800}>{t('oversight.impersonate.duration30')}</option>
          <option value={DEFAULT_IMPERSONATION_DURATION_SECONDS}>
            {t('oversight.impersonate.duration60')}
          </option>
        </select>
      </div>

      {impersonate.isError && (
        <p data-testid="impersonate-error" className="text-sm text-[var(--color-alert)]">
          {t('oversight.impersonate.error')}
        </p>
      )}

      {impersonate.isSuccess && (
        <p data-testid="impersonate-started" className="text-sm text-[var(--color-success)]">
          {t('oversight.impersonate.started')}
        </p>
      )}

      <Button
        data-testid="impersonate-start-btn"
        size="sm"
        className="self-start"
        disabled={!canStart}
        onClick={start}
      >
        {impersonate.isPending
          ? t('oversight.impersonate.starting')
          : t('oversight.impersonate.start')}
      </Button>
    </Card>
  );
}
